/** The base DXF document a drawing is rebuilt on top of — phase 3.
 *
 * ⚠⚠ **THE ONE PART OF `rows_input`'s `meta` THAT CANNOT COME FROM THE DRAWING.** Everything else
 * `Store::rows_meta` builds from the rows it holds; `base` is
 * `ezdxf.new(version, setup).export_sections()` — the document `export_dxf` starts from before a
 * single client row lands in it — and there is no ezdxf in a browser. So it ships as a static
 * asset, written by `manage.py base_documents`.
 *
 * ⚠⚠ **`setup` IS NOT A PREFERENCE. IT IS WHETHER THE DRAWING BROUGHT ITS OWN SYMBOL TABLE.**
 * `display_rs.rows_input` passes `not has_tables`: a drawing with no table entries of its own
 * starts from ezdxf's set-up document (linetypes, text styles, dimension styles), and one that
 * brought its own must NOT, or ezdxf's defaults shadow the client's. Get this backwards and the
 * drawing still draws — with the wrong linetypes.
 *
 * ⚠ 14–55 KB each, 3.5–6.4 KB gzipped, and a tab fetches exactly one. Cached here so opening a
 * second drawing of the same version costs nothing.
 */

const cache = new Map<string, Promise<string>>();

/** The file name, spelt exactly as `base_documents.name_of` spells it. */
export function baseName(version: string, setup: boolean): string {
  return `${version || "R2013"}-${setup ? "setup" : "plain"}.dxf`;
}

/** Fetch it. ⚠ Throws with the name in the message: a missing base is a drawing that will not
 *  draw, and "404" alone would send the next reader looking in the wrong place. */
export function baseDocument(version: string, setup: boolean,
                             root = "/base/"): Promise<string> {
  const name = baseName(version, setup);
  let got = cache.get(name);
  if (!got) {
    got = fetch(root + name, { credentials: "same-origin" }).then((r) => {
      if (!r.ok) {
        cache.delete(name);                    // ⚠ so a retry can succeed after a deploy
        throw new Error(`the base document ${name} is not there (${r.status}) — `
                        + "run manage.py base_documents");
      }
      return r.text();
    });
    cache.set(name, got);
  }
  return got;
}

export function clearBaseCache(): void {
  cache.clear();
}
